import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Button, 
  Tag, 
  Typography, 
  Space, 
  Avatar, 
  Input, 
  Select, 
  Row, 
  Col, 
  Divider,
  Timeline,
  Badge,
  Tooltip,
  Modal,
  Form,
  message,
  Table
} from 'antd';
import { 
  ArrowLeftOutlined,
  UserOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MessageOutlined,
  SendOutlined,
  EditOutlined,
  FileTextOutlined,
  PhoneOutlined,
  MailOutlined,
  SearchOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const TicketDetail = () => {
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [isNoteModalVisible, setIsNoteModalVisible] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [reassignOpen, setReassignOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [productOpen, setProductOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productStatus, setProductStatus] = useState('all');
  const [productList, setProductList] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [macros, setMacros] = useState([]);
  const [operators, setOperators] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [refreshingMessages, setRefreshingMessages] = useState(false);
  const [form] = Form.useForm();
  const messagesContainerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeId } = useParams();
  const id = routeId
    || (location.pathname.match(/\/tickets\/(\d+)/)?.[1])
    || '';
  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const scrollMessagesToBottom = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  };

  useEffect(() => {
    scrollMessagesToBottom();
  }, [messages]);

  useEffect(() => {
    const boot = async () => {
      try {
        await axios.post('https://limitverify.ru/api/email/sync', null, { headers: authHeaders });
      } catch (_) { /* ignore */ }
      loadTicket();
      loadMessages();
      loadMacros();
      loadOperators();
    };
    boot();
  }, [id]);

  const loadMacros = async () => {
    try {
      const res = await axios.get('https://limitverify.ru/api/tickets/macros', { headers: authHeaders });
      if (res.data.success) setMacros(res.data.macros || []);
    } catch (_) { /* ignore */ }
  };

  const loadOperators = async () => {
    try {
      const res = await axios.get('https://limitverify.ru/api/tickets/operators', { headers: authHeaders });
      if (res.data.success) setOperators(res.data.operators || []);
    } catch (_) { /* ignore */ }
  };

  const loadTicket = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const token = localStorage.getItem('token');
      let response = await axios.get(`https://limitverify.ru/api/tickets/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setTicket(response.data.ticket);
        setNotes(response.data.ticket.notes || []);
      } else {
        // fallback на порт 5000
        response = await axios.get(`https://limitverify.ru:5000/api/tickets/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setTicket(response.data.ticket);
          setNotes(response.data.ticket.notes || []);
        } else {
          setLoadError(response.data.error || 'Ошибка загрузки тикета');
          message.error(response.data.error || 'Ошибка загрузки тикета');
        }
      }
    } catch (error) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`https://limitverify.ru:5000/api/tickets/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setTicket(response.data.ticket);
          setNotes(response.data.ticket.notes || []);
        } else {
          setLoadError(response.data.error || 'Ошибка загрузки тикета');
          message.error(response.data.error || 'Ошибка загрузки тикета');
        }
      } catch (e) {
        console.error('Ошибка загрузки тикета:', e);
        setLoadError('Ошибка загрузки тикета');
        message.error('Ошибка загрузки тикета');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async ({ silent = true } = {}) => {
    try {
      const token = localStorage.getItem('token');
      let response = await axios.get(`https://limitverify.ru/api/tickets/${id}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setMessages(response.data.messages || []);
        if (!silent) message.success('Переписка обновлена');
      } else {
        response = await axios.get(`https://limitverify.ru:5000/api/tickets/${id}/messages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setMessages(response.data.messages || []);
          if (!silent) message.success('Переписка обновлена');
        }
      }
    } catch (error) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`https://limitverify.ru:5000/api/tickets/${id}/messages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setMessages(response.data.messages || []);
          if (!silent) message.success('Переписка обновлена');
          return;
        }
      } catch (e) {
        console.error('Ошибка загрузки сообщений:', e);
      }
      if (!silent) message.error('Не удалось обновить переписку');
      setMessages([]);
    }
  };

  const handleRefreshMessages = async () => {
    setRefreshingMessages(true);
    try {
      try {
        await axios.post('https://limitverify.ru/api/email/sync?force=1', null, { headers: authHeaders });
      } catch (syncErr) {
        console.warn('email sync failed, loading messages anyway', syncErr?.message);
      }
      await Promise.all([loadTicket(), loadMessages({ silent: false })]);
    } finally {
      setRefreshingMessages(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      new: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Новый' },
      open: { color: 'blue', icon: <ClockCircleOutlined />, text: 'Открыт' },
      in_progress: { color: 'blue', icon: <ClockCircleOutlined />, text: 'В работе' },
      pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Ждём клиента' },
      resolved: { color: 'cyan', icon: <CheckCircleOutlined />, text: 'Решён' },
      closed: { color: 'green', icon: <CheckCircleOutlined />, text: 'Закрыт' },
      cancelled: { color: 'gray', icon: <CloseCircleOutlined />, text: 'Отменен' }
    };
    return configs[status] || configs.new;
  };

  const getPriorityConfig = (priority) => {
    const configs = {
      1: { color: 'red', text: 'Критический' },
      2: { color: 'orange', text: 'Высокий' },
      3: { color: 'blue', text: 'Средний' },
      4: { color: 'cyan', text: 'Низкий' },
      5: { color: 'green', text: 'Минимальный' }
    };
    return configs[priority] || configs[3];
  };

  const getSourceConfig = (source) => {
    const configs = {
      telegram: { color: 'blue', text: 'Telegram' },
      form: { color: 'green', text: 'Форма' },
      email: { color: 'purple', text: 'Email' },
      phone: { color: 'orange', text: 'Телефон' }
    };
    return configs[source] || configs.form;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleClaim = async () => {
    try {
      const res = await axios.post(`https://limitverify.ru/api/tickets/${id}/claim`, {}, { headers: authHeaders });
      if (res.data.success) {
        message.success('Тикет взят');
        await loadTicket();
      }
    } catch (e) {
      message.error('Не удалось взять тикет');
    }
  };

  const handleAssign = async (userId) => {
    try {
      await axios.put(`https://limitverify.ru/api/tickets/${id}`, {
        assigned_to: userId || null
      }, { headers: authHeaders });
      message.success(userId ? 'Назначен' : 'Назначение снято');
      await loadTicket();
    } catch (e) {
      message.error('Ошибка назначения');
    }
  };

  const applyMacro = (macroId) => {
    const macro = macros.find(m => m.id === macroId);
    if (macro) setNewMessage(macro.body);
  };

  const handleSaveTags = async (tags) => {
    try {
      await axios.put(`https://limitverify.ru/api/tickets/${id}`, { tags }, { headers: authHeaders });
      setTicket({ ...ticket, tags });
      message.success('Теги обновлены');
    } catch (e) {
      message.error('Ошибка сохранения тегов');
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    const next = Array.from(new Set([...(ticket.tags || []), t]));
    setTagInput('');
    handleSaveTags(next);
  };

  const removeTag = (tag) => {
    handleSaveTags((ticket.tags || []).filter(x => x !== tag));
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`https://limitverify.ru/api/tickets/${id}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const updatedAt = response.data.ticket?.updated_at || new Date().toISOString();
        setTicket({ ...ticket, status: newStatus, updated_at: updatedAt });
        message.success('Статус обновлен');
      } else {
        message.error(response.data.error || 'Не удалось обновить статус');
      }
    } catch (err) {
      console.error('Ошибка обновления статуса:', err?.response?.data || err);
      message.error(err?.response?.data?.error || 'Ошибка обновления статуса');
    }
  };

  const handlePriorityChange = async (newPriority) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`https://limitverify.ru/api/tickets/${id}`, {
        priority: Number(newPriority)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const updatedAt = response.data.ticket?.updated_at || new Date().toISOString();
        setTicket({ ...ticket, priority: newPriority, updated_at: updatedAt });
        message.success('Приоритет обновлен');
      } else {
        message.error(response.data.error || 'Не удалось обновить приоритет');
      }
    } catch (err) {
      console.error('Ошибка обновления приоритета:', err?.response?.data || err);
      message.error(err?.response?.data?.error || 'Ошибка обновления приоритета');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`https://limitverify.ru/api/tickets/${id}/messages`, {
        message_text: newMessage,
        is_internal: false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Перезагружаем сообщения после отправки
        await loadMessages();
        setNewMessage('');
        message.success('Сообщение отправлено');
      } else {
        message.error('Ошибка отправки сообщения');
      }
    } catch (error) {
      console.error('Ошибка отправки сообщения:', error);
      message.error('Ошибка отправки сообщения');
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`https://limitverify.ru/api/tickets/${id}/notes`, {
        note_text: noteText,
        is_internal: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        // добавить в список без перезагрузки
        setNotes([response.data.note, ...notes]);
        setNoteText('');
        setIsNoteModalVisible(false);
        message.success('Заметка добавлена');
      } else {
        message.error('Не удалось добавить заметку');
      }
    } catch (error) {
      console.error('Ошибка добавления заметки:', error);
      message.error('Ошибка добавления заметки');
    }
  };

  const handleViewClient = () => {
    if (!ticket?.client_id) {
      message.warning('Клиент не привязан к тикету');
      return;
    }
    navigate(`/clients/${ticket.client_id}`, {
      state: { fromTicketId: ticket.id }
    });
  };

  // Поиск клиентов для перевыбора
  const searchClients = async (query) => {
    try {
      const resp = await axios.get(`https://limitverify.ru:5000/api/clients`, {
        params: { page: 1, limit: 10, search: query, status: 'active' },
        headers: authHeaders
      });
      const options = (resp.data.clients || []).map(c => {
        let name = c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim();
        if (!name) {
          const emailLocal = c.email ? String(c.email).split('@')[0] : '';
          name = emailLocal || (c.telegram_nickname ? `@${c.telegram_nickname}` : 'Без имени');
        }
        const extra = c.email || (c.telegram_nickname ? `@${c.telegram_nickname}` : '');
        return {
          value: c.id,
          label: `${name}${extra ? ` • ${extra}` : ''}`
        };
      });
      setClientOptions(options);
    } catch (e) {
      setClientOptions([]);
    }
  };

  const openReassign = () => {
    setClientSearch('');
    setClientOptions([]);
    setSelectedClient(null);
    setReassignOpen(true);
  };

  const handleReassign = async () => {
    if (!selectedClient) return;
    try {
      const resp = await axios.put(`https://limitverify.ru:5000/api/tickets/${id}/reassign-client`, {
        client_id: selectedClient
      }, { headers: authHeaders });
      if (resp.data.success) {
        message.success('Клиент обновлен');
        setReassignOpen(false);
        await loadTicket();
      } else {
        message.error(resp.data.error || 'Не удалось сменить клиента');
      }
    } catch (e) {
      message.error('Ошибка смены клиента');
    }
  };

  const handleViewProduct = () => {
    if (!ticket?.product_id) {
      message.warning('Сначала выберите изделие');
      return;
    }
    navigate(`/products/${ticket.product_id}`, {
      state: { fromTicketId: ticket.id }
    });
  };

  const loadProductsForPicker = async (search = productSearch, status = productStatus) => {
    setProductLoading(true);
    try {
      const resp = await axios.get(`https://limitverify.ru:5000/api/products`, {
        params: {
          page: 1,
          limit: 100,
          search: search || '',
          status: status !== 'all' ? status : '',
          sortBy: 'createdAt',
          sortOrder: 'DESC'
        },
        headers: authHeaders
      });
      setProductList(resp.data.products || []);
    } catch (e) {
      console.error('Ошибка загрузки изделий:', e);
      setProductList([]);
      message.error('Не удалось загрузить список изделий');
    } finally {
      setProductLoading(false);
    }
  };

  const openReassignProduct = () => {
    setProductOpen(true);
    setSelectedProduct(ticket?.product_id || null);
    setProductSearch('');
    setProductStatus('all');
    loadProductsForPicker('', 'all');
  };

  const handleReassignProduct = async () => {
    if (!selectedProduct) {
      message.warning('Выберите изделие из списка');
      return;
    }
    try {
      const resp = await axios.put(`https://limitverify.ru:5000/api/tickets/${id}/reassign-product`, {
        product_id: selectedProduct
      }, { headers: authHeaders });
      if (resp.data.success) {
        message.success('Изделие привязано');
        setProductOpen(false);
        const updated = { ...ticket, product_id: resp.data.ticket.product_id, product: resp.data.ticket.product };
        setTicket(updated);
        await loadTicket();
      } else {
        message.error(resp.data.error || 'Не удалось привязать изделие');
      }
    } catch (e) {
      message.error('Ошибка привязки изделия');
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Загрузка...</div>;
  }

  if (!ticket) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        {loadError ? `Ошибка: ${loadError}` : 'Тикет не найден'}
      </div>
    );
  }

  const statusConfig = getStatusConfig(ticket.status);
  const priorityConfig = getPriorityConfig(ticket.priority);
  const sourceConfig = getSourceConfig(ticket.source);

  return (
    <div style={{ padding: '24px' }}>
      {/* Заголовок */}
      <div style={{ marginBottom: '24px' }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/clients')}
          style={{ marginRight: '16px' }}
        >
          Назад к списку
        </Button>
        <Title level={2} style={{ margin: 0, display: 'inline' }}>
          Тикет #{ticket.id}
        </Title>
        <Space style={{ marginLeft: 16 }}>
          {!ticket.assigned_to_id && (
            <Button type="primary" onClick={handleClaim}>Взять себе</Button>
          )}
          <Select
            placeholder="Назначить"
            style={{ width: 200 }}
            allowClear
            value={ticket.assigned_to_id || undefined}
            onChange={handleAssign}
            options={[
              ...operators.map(o => ({ value: o.id, label: o.name }))
            ]}
          />
        </Space>
      </div>

      <Row gutter={24}>
        {/* Основная информация */}
        <Col span={16}>
          {/* Информация о тикете */}
          <Card title="Информация о тикете" style={{ marginBottom: '16px' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Тема:</Text>
                    <br />
                    <Text>{ticket.subject}</Text>
                  </div>
                  <div>
                    <Text strong>Описание:</Text>
                    <br />
                    <Paragraph>{ticket.description}</Paragraph>
                  </div>
                  <div>
                    <Text strong>Статус:</Text>
                    <br />
                    <Select
                      value={ticket.status === 'in_progress' ? 'open' : ticket.status}
                      onChange={handleStatusChange}
                      style={{ width: 180 }}
                    >
                      <Option value="new">Новый</Option>
                      <Option value="open">Открыт</Option>
                      <Option value="pending">Ждём клиента</Option>
                      <Option value="resolved">Решён</Option>
                      <Option value="closed">Закрыт</Option>
                      <Option value="cancelled">Отменен</Option>
                    </Select>
                  </div>
                  <div>
                    <Text strong>Теги:</Text>
                    <br />
                    <Space wrap style={{ marginTop: 4 }}>
                      {(ticket.tags || []).map(tag => (
                        <Tag key={tag} closable onClose={() => removeTag(tag)}>{tag}</Tag>
                      ))}
                      <Input
                        size="small"
                        style={{ width: 120 }}
                        placeholder="+ тег"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onPressEnter={addTag}
                      />
                    </Space>
                  </div>
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Приоритет:</Text>
                    <br />
                    <Select
                      value={ticket.priority}
                      onChange={handlePriorityChange}
                      style={{ width: 150 }}
                    >
                      <Option value={1}>Критический</Option>
                      <Option value={2}>Высокий</Option>
                      <Option value={3}>Средний</Option>
                      <Option value={4}>Низкий</Option>
                      <Option value={5}>Минимальный</Option>
                    </Select>
                  </div>
                  <div>
                    <Text strong>Источник:</Text>
                    <br />
                    <Tag color={sourceConfig.color}>{sourceConfig.text}</Tag>
                  </div>
                  <div>
                    <Text strong>Создан:</Text>
                    <br />
                    <Text>{formatDate(ticket.created_at)}</Text>
                  </div>
                  <div>
                    <Text strong>Обновлен:</Text>
                    <br />
                    <Text>{formatDate(ticket.updated_at)}</Text>
                  </div>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Переписка */}
          <Card
            title={`Переписка · Тикет #${ticket.id}`}
            style={{ marginBottom: '16px' }}
            extra={
              <Button
                type="primary"
                danger
                icon={<ReloadOutlined />}
                loading={refreshingMessages}
                onClick={handleRefreshMessages}
              >
                Обновить
              </Button>
            }
          >
            <div
              ref={messagesContainerRef}
              style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '16px' }}
            >
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  style={{
                    display: 'flex',
                    marginBottom: '16px',
                    justifyContent: msg.sender_type === 'client' ? 'flex-start' : 'flex-end'
                  }}
                >
                  <div
                    style={{
                      maxWidth: '70%',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      backgroundColor: msg.sender_type === 'client' ? '#f0f0f0' : '#1890ff',
                      color: msg.sender_type === 'client' ? '#000' : '#fff'
                    }}
                  >
                    <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '4px' }}>
                      {msg.sender_name} • {formatDate(msg.created_at)}
                    </div>
                    <div>{msg.message_text}</div>
                  </div>
                </div>
              ))}
            </div>
            
            <div style={{ display: 'flex', gap: '8px', marginBottom: 8 }}>
              <Select
                placeholder="Макрос"
                style={{ width: 220 }}
                allowClear
                onChange={applyMacro}
                options={macros.map(m => ({ value: m.id, label: m.title }))}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input.TextArea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Введите сообщение..."
                rows={3}
                style={{ flex: 1 }}
              />
              <Space direction="vertical">
                <Button 
                  type="primary" 
                  icon={<SendOutlined />}
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                >
                  Отправить
                </Button>
                <Button
                  onClick={async () => {
                    await handleStatusChange('pending');
                  }}
                >
                  Ждём клиента
                </Button>
              </Space>
            </div>
          </Card>
        </Col>

        {/* Боковая панель */}
        <Col span={8}>
          {/* Информация о клиенте */}
          <Card title="Клиент" style={{ marginBottom: '16px' }} extra={<Button type="link" onClick={openReassign}>Сменить</Button>}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>{ticket.client?.full_name || 'Не указано'}</Text>
                <br />
                <Text type="secondary">{ticket.client?.email || 'Не указано'}</Text>
                <br />
                <Text type="secondary">{ticket.client?.phone || 'Не указано'}</Text>
              </div>
              <Button 
                type="primary" 
                icon={<UserOutlined />}
                onClick={handleViewClient}
                disabled={!ticket.client_id}
                block
              >
                Просмотреть карточку клиента
              </Button>
            </Space>
          </Card>

          {/* Информация об изделии */}
          <Card title="Изделие" style={{ marginBottom: '16px' }} extra={<Button type="link" onClick={openReassignProduct}>Сменить</Button>}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>{ticket.product?.name || 'Не указано'}</Text>
                <br />
                <Text type="secondary">Серийный номер: {ticket.product?.serial_number || 'Не указано'}</Text>
              </div>
              <Tooltip title={!ticket.product_id ? 'Сначала выберите изделие' : ''}>
                <Button 
                  type="primary" 
                  icon={<FileTextOutlined />}
                  onClick={handleViewProduct}
                  disabled={!ticket.product_id}
                  block
                >
                  Просмотреть карточку изделия
                </Button>
              </Tooltip>
            </Space>
          </Card>

          {/* Заметки */}
          <Card 
            title="Заметки" 
            extra={
              <Button 
                type="link" 
                icon={<EditOutlined />}
                onClick={() => setIsNoteModalVisible(true)}
              >
                Добавить
              </Button>
            }
          >
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {notes.map((note) => (
                <div key={note.id} style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '6px' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    {note.employee_name} • {formatDate(note.created_at)}
                  </div>
                  <div>{note.note_text || note.noteText}</div>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Модальное окно для добавления заметки */}
      <Modal
        title="Добавить заметку"
        open={isNoteModalVisible}
        onOk={handleAddNote}
        onCancel={() => setIsNoteModalVisible(false)}
        okText="Добавить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Текст заметки">
            <TextArea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Введите заметку..."
              rows={4}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно смены клиента */}
      <Modal
        title="Сменить клиента"
        open={reassignOpen}
        onOk={handleReassign}
        onCancel={() => setReassignOpen(false)}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong>Новый клиент</Text>
            <Select
              showSearch
              value={selectedClient}
              placeholder="Найти клиента..."
              onSearch={(v) => { setClientSearch(v); searchClients(v); }}
              onChange={(v) => setSelectedClient(v)}
              filterOption={false}
              options={clientOptions}
              style={{ width: '100%', marginTop: 8 }}
            />
          </div>
        </Space>
      </Modal>

      {/* Модальное окно смены изделия */}
      <Modal
        title="Выбрать изделие"
        open={productOpen}
        onOk={handleReassignProduct}
        onCancel={() => setProductOpen(false)}
        okText="Привязать"
        cancelText="Отмена"
        okButtonProps={{ disabled: !selectedProduct }}
        width={900}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Row gutter={12}>
            <Col xs={24} md={14}>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Поиск по названию, серийному или ID номеру"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                onPressEnter={() => loadProductsForPicker(productSearch, productStatus)}
              />
            </Col>
            <Col xs={24} md={6}>
              <Select
                value={productStatus}
                onChange={(v) => {
                  setProductStatus(v);
                  loadProductsForPicker(productSearch, v);
                }}
                style={{ width: '100%' }}
              >
                <Option value="all">Все статусы</Option>
                <Option value="АКТИВНО">Активные</Option>
                <Option value="АРХИВ">Архив</Option>
                <Option value="АННУЛИРОВАНО">Аннулировано</Option>
              </Select>
            </Col>
            <Col xs={24} md={4}>
              <Button
                type="primary"
                block
                onClick={() => loadProductsForPicker(productSearch, productStatus)}
                loading={productLoading}
              >
                Найти
              </Button>
            </Col>
          </Row>

          <Table
            rowKey="id"
            loading={productLoading}
            dataSource={productList}
            size="small"
            pagination={{ pageSize: 8, showSizeChanger: false }}
            scroll={{ y: 360 }}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: selectedProduct ? [selectedProduct] : [],
              onChange: (keys) => setSelectedProduct(keys[0] || null)
            }}
            onRow={(record) => ({
              onClick: () => setSelectedProduct(record.id),
              style: { cursor: 'pointer' }
            })}
            columns={[
              {
                title: 'Название',
                dataIndex: 'name',
                key: 'name',
                ellipsis: true
              },
              {
                title: 'Серийный №',
                dataIndex: 'serialNumber',
                key: 'serialNumber',
                width: 140,
                render: (v) => v || '—'
              },
              {
                title: 'ID номер',
                dataIndex: 'identificationNumber',
                key: 'identificationNumber',
                width: 140,
                render: (v) => v || '—'
              },
              {
                title: 'Статус',
                dataIndex: 'status',
                key: 'status',
                width: 110,
                render: (status) => (
                  <Tag color={status === 'АКТИВНО' ? 'green' : 'default'}>
                    {status || '—'}
                  </Tag>
                )
              },
              {
                title: 'Владелец',
                key: 'owner',
                ellipsis: true,
                render: (_, record) =>
                  record.client?.fullName ||
                  (record.client
                    ? `${record.client.firstName || ''} ${record.client.lastName || ''}`.trim()
                    : '—')
              }
            ]}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default TicketDetail;
